import { getPayoutDate, getWeekPeriod, isDateInside, timeToMinutes } from './date'

function num(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function findRateForDate(rates, dateISO) {
  if (!rates?.length) return { hourlyRate: 0, orderRate: 0, effectiveFrom: '' }
  const sorted = [...rates].sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)))
  return sorted.find((rate) => rate.effectiveFrom <= dateISO) || sorted[sorted.length - 1]
}

function normalizeEndMinutes(minutes) {
  return minutes === 0 ? 24 * 60 : minutes
}

function getShiftTimeSpan(shift) {
  if (!shift.startTime || !shift.endTime) return null
  const start = timeToMinutes(shift.startTime)
  let end = timeToMinutes(shift.endTime)
  if (start === null || end === null) return null

  end = normalizeEndMinutes(end)
  if (end <= start) end += 24 * 60
  return { start, end, minutes: Math.max(0, end - start) }
}

function hoursAfterTime(shift, timeFrom) {
  const span = getShiftTimeSpan(shift)
  if (!span || !timeFrom) return 0
  const threshold = timeToMinutes(timeFrom)
  if (threshold === null) return 0

  let thresholdInShiftDay = normalizeEndMinutes(threshold)
  if (thresholdInShiftDay < span.start && span.start >= 12 * 60) thresholdInShiftDay += 24 * 60

  const overlapStart = Math.max(span.start, thresholdInShiftDay)
  const overlapMinutes = Math.max(0, span.end - overlapStart)
  return Math.round((overlapMinutes / 60) * 100) / 100
}

function hoursInsideTimeWindow(shift, timeFrom, timeTo) {
  const span = getShiftTimeSpan(shift)
  if (!span || !timeFrom || !timeTo) return 0

  const from = timeToMinutes(timeFrom)
  let to = timeToMinutes(timeTo)
  if (from === null || to === null) return 0

  let windowStart = normalizeEndMinutes(from)
  let windowEnd = normalizeEndMinutes(to)
  if (windowEnd <= windowStart) windowEnd += 24 * 60

  if (windowStart < span.start && span.start >= 12 * 60) {
    windowStart += 24 * 60
    windowEnd += 24 * 60
  }

  const overlapStart = Math.max(span.start, windowStart)
  const overlapEnd = Math.min(span.end, windowEnd)
  const overlapMinutes = Math.max(0, overlapEnd - overlapStart)
  return Math.round((overlapMinutes / 60) * 100) / 100
}

function ordersInsideTimeWindow(shift, timeFrom, timeTo, orders, hours) {
  const windowHours = hoursInsideTimeWindow(shift, timeFrom, timeTo)
  if (!windowHours || !hours) return 0
  if (windowHours >= hours) return orders
  return Math.round(((orders * windowHours) / hours) * 100) / 100
}

function ruleApplies(rule, shift) {
  if (!rule.active) return false
  if (!isDateInside(shift.date, rule.effectiveFrom, rule.effectiveTo)) return false
  if (rule.conditionCode && !shift.conditionCodes?.includes(rule.conditionCode)) return false
  return true
}

export function calculateShift(shift, { rates = [], bonusRules = [], taxRules = [], settings = {} } = {}) {
  const rate = findRateForDate(rates, shift.date)
  const hours = num(shift.hours)
  const orders = num(shift.orders)
  const hourlyIncome = hours * num(rate.hourlyRate)
  const orderIncome = orders * num(rate.orderRate)
  const baseIncome = hourlyIncome + orderIncome

  const appliedBonuses = bonusRules.filter((rule) => ruleApplies(rule, shift)).map((rule) => {
    let quantity = 1
    let amount = num(rule.amount)
    let total = amount
    let detail = 'фиксированная сумма'

    if (rule.type === 'per_order') {
      quantity = orders
      total = quantity * amount
      detail = `${quantity} заказов × ${amount}`
    }

    if (rule.type === 'per_hour') {
      quantity = hours
      total = quantity * amount
      detail = `${quantity} ч × ${amount}`
    }

    if (rule.type === 'after_time') {
      quantity = hoursAfterTime(shift, rule.timeFrom)
      total = quantity * amount
      detail = `${quantity} ч после ${rule.timeFrom} × ${amount}`
    }

    if (rule.type === 'time_hour') {
      quantity = hoursInsideTimeWindow(shift, rule.timeFrom, rule.timeTo)
      total = quantity * amount
      detail = `${quantity} ч в интервале ${rule.timeFrom}–${rule.timeTo} × ${amount}`
    }

    if (rule.type === 'time_order') {
      quantity = ordersInsideTimeWindow(shift, rule.timeFrom, rule.timeTo, orders, hours)
      total = quantity * amount
      detail = `${quantity} заказов в интервале ${rule.timeFrom}–${rule.timeTo} × ${amount}`
    }

    if (rule.type === 'fixed_shift') {
      detail = 'за смену'
    }

    return {
      id: rule.id,
      title: rule.title,
      type: rule.type,
      amount,
      quantity,
      total,
      detail
    }
  })

  const bonusesTotal = appliedBonuses.reduce((sum, bonus) => sum + bonus.total, 0)
  const manualAdjustment = num(shift.manualAdjustment)
  const gross = baseIncome + bonusesTotal + manualAdjustment

  const activeTaxes = taxRules.filter((rule) => {
    if (!rule.active) return false
    return isDateInside(shift.date, rule.effectiveFrom, rule.effectiveTo)
  })

  const taxItems = activeTaxes.map((rule) => {
    const base = getTaxBase(rule.base, {
      gross,
      baseIncome,
      bonusesTotal,
      hourlyIncome,
      orderIncome
    })
    const value = num(rule.value)
    const total = rule.type === 'percentage' ? roundMoney((base * value) / 100) : value
    return {
      id: rule.id,
      title: rule.title,
      mode: rule.mode,
      type: rule.type,
      value,
      base,
      total,
      detail: rule.type === 'percentage' ? `${value}% от ${roundMoney(base)}` : 'фиксированная сумма'
    }
  })

  const deductionsTotal = taxItems.filter((item) => item.mode === 'deduction').reduce((sum, item) => sum + item.total, 0)
  const compensationsTotal = taxItems.filter((item) => item.mode === 'compensation').reduce((sum, item) => sum + item.total, 0)
  const net = gross - deductionsTotal

  return {
    shiftId: shift.id,
    rate,
    hours,
    orders,
    hourlyIncome: roundMoney(hourlyIncome),
    orderIncome: roundMoney(orderIncome),
    baseIncome: roundMoney(baseIncome),
    appliedBonuses,
    bonusesTotal: roundMoney(bonusesTotal),
    manualAdjustment: roundMoney(manualAdjustment),
    gross: roundMoney(gross),
    taxItems,
    deductionsTotal: roundMoney(deductionsTotal),
    compensationsTotal: roundMoney(compensationsTotal),
    net: roundMoney(net)
  }
}

function getTaxBase(base, values) {
  switch (base) {
    case 'base': return values.baseIncome
    case 'bonuses': return values.bonusesTotal
    case 'hours': return values.hourlyIncome
    case 'orders': return values.orderIncome
    case 'gross':
    default: return values.gross
  }
}

export function roundMoney(value) {
  return Math.round((num(value) + Number.EPSILON) * 100) / 100
}

export function summarizeCalculated(calculatedItems) {
  return calculatedItems.reduce((acc, item) => {
    acc.hours += item.shift.hours
    acc.orders += item.shift.orders
    acc.baseIncome += item.calculation.baseIncome
    acc.bonusesTotal += item.calculation.bonusesTotal
    acc.manualAdjustment += item.calculation.manualAdjustment
    acc.gross += item.calculation.gross
    acc.deductionsTotal += item.calculation.deductionsTotal
    acc.compensationsTotal += item.calculation.compensationsTotal
    acc.net += item.calculation.net
    return acc
  }, {
    hours: 0,
    orders: 0,
    baseIncome: 0,
    bonusesTotal: 0,
    manualAdjustment: 0,
    gross: 0,
    deductionsTotal: 0,
    compensationsTotal: 0,
    net: 0
  })
}

export function groupByPayoutPeriod(calculatedItems, settings) {
  const map = new Map()
  for (const item of calculatedItems) {
    const period = getWeekPeriod(item.shift.date, settings.weekStartsOn)
    if (!map.has(period.key)) {
      map.set(period.key, {
        ...period,
        payoutDate: getPayoutDate(period.end, settings),
        items: []
      })
    }
    map.get(period.key).items.push(item)
  }

  return [...map.values()]
    .map((period) => ({
      ...period,
      summary: summarizeCalculated(period.items)
    }))
    .sort((a, b) => b.start.localeCompare(a.start))
}
